import { ApolloClient, ApolloProvider, InMemoryCache } from '@apollo/client'
import { Box } from '@mui/material'
import { StyledEngineProvider, Theme, ThemeProvider } from '@mui/material/styles'
import { LoadingType, PlatformProvider, usePlatform } from '@zippie/did-react-components'
import { Suspense, useEffect, useMemo, useState } from 'react'
import {
  Navigate,
  Outlet,
  Route,
  RouterProvider,
  createBrowserRouter,
  createRoutesFromElements,
} from 'react-router-dom'

import CreatePopUpComponent from './components/CreatePopUp'
import { PreloadWrapper, lazyWithPreload } from './components/LazyRoute'
import { LoadingProgress } from './components/LoadingProgress'
import { Sidebar } from './components/Sidebar'
import SimpleSnackbar from './components/Snackbar'
import * as routes from './constants/routes'
import { OrganizationContainer } from './hooks/useOrganization'
import { ProjectsContainer } from './hooks/useProjects'
import { SnackBarContainer } from './hooks/useSnackBar'
import {
  SUB_NAV_COLLAPSED_WIDTH,
  SUB_NAV_EXPANDED_WIDTH,
  SubNavigationContainer,
  useSubNavigationContext,
} from './hooks/useSubNavigation'
import { TierSelectionContainer } from './hooks/useTierSelection'
import { TranslationsProvider } from './hooks/useTranslations'
import { UsersContainer } from './hooks/useUsers'
import { WorkspaceContainer, useWorkspaceContext } from './hooks/useWorkspace'
import { ZippieIdProvider, useZippieIdContext } from './hooks/useZippieId'
import { customTheme, theme } from './Theme'
import RootBoundary from './views/error-pages/RootBoundary'
import TierSelectionView from './views/TierSelectionView'
import TransferOwnershipInviteView from './views/transfer-ownership/TransferOwnershipInviteView'
import WelcomeView from './views/WelcomeView'
import CreateAdditionalWorkspaceModal from './views/workspace/modals/CreateAdditionalWorkspaceModal'
import CreateWorkspaceModal from './views/workspace/modals/CreateWorkspaceModal'

const AllProjectsView = lazyWithPreload(() => import('./views/AllProjectsView'))
const CreateSuperAdministrator = lazyWithPreload(() => import('./views/CreateSuperAdminView'))
const AdminView = lazyWithPreload(() => import('./views/workspace/admin/AdminView'))

const PeopleView = lazyWithPreload(() => import('./views/people/PeopleView'))
const PersonView = lazyWithPreload(() => import('./views/people/PersonView'))
const ProjectView = lazyWithPreload(() => import('./views/ProjectView'))
const SuperAdministrators = lazyWithPreload(() => import('./views/SuperAdministratorsView'))
const WorkspaceView = lazyWithPreload(() => import('./views/workspace/WorkspaceView'))

const AccountSettingsView = lazyWithPreload(() => import('./views/account-settings/AccountSettingsView'))

declare module '@mui/styles/defaultTheme' {
  // eslint-disable-next-line @typescript-eslint/no-empty-interface
  interface DefaultTheme extends Theme {}
}

function CreateWorkspace() {
  const { isCreateWorkspaceModal, isCreateAdditionalWorkspaceModal, setIsCreateAdditionalWorkspaceModal } =
    useWorkspaceContext()
  return (
    <>
      {isCreateWorkspaceModal && (
        <CreatePopUpComponent
          handleClose={() => null}
          disableEscapeKeyDown
          open
          maxWidth="478px"
          fullHeight
          sx={{
            width: '100%',
            backdropFilter: 'blur(5px)',
          }}
        >
          <CreateWorkspaceModal />
        </CreatePopUpComponent>
      )}
      {isCreateAdditionalWorkspaceModal && (
        <CreatePopUpComponent
          open={isCreateAdditionalWorkspaceModal}
          handleClose={() => {
            setIsCreateAdditionalWorkspaceModal(false)
            localStorage.removeItem('invite_owner')
          }}
          maxWidth="478px"
          fullHeight
          sx={{
            width: '100%',
            backdropFilter: 'blur(5px)',
          }}
        >
          <CreateAdditionalWorkspaceModal />
        </CreatePopUpComponent>
      )}
    </>
  )
}
function Layout() {
  const { isSubNavOpen, isSubNavCollapsed } = useSubNavigationContext()

  const additionalWidth = isSubNavCollapsed ? SUB_NAV_COLLAPSED_WIDTH : SUB_NAV_EXPANDED_WIDTH
  const marginLeft = !isSubNavOpen ? 220 : 48 + additionalWidth

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', backgroundColor: !isSubNavOpen ? 'grey.900' : 'grey.800' }}>
      <Sidebar />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: `calc(100% - ${marginLeft}px)`,
          marginY: 1,
          backgroundColor: 'white',
          borderTopLeftRadius: '12px',
          borderBottomLeftRadius: '12px',
          marginLeft: `${marginLeft}px`,
        }}
      >
        <Outlet />
      </Box>
    </Box>
  )
}

function RootLayout() {
  const { accessToken } = useZippieIdContext()

  const client = new ApolloClient({
    uri: 'https://api.dev.airimpact.co/graphql',
    cache: new InMemoryCache(),
    headers: {
      'x-access-token': accessToken,
    },
  })

  return (
    <ApolloProvider client={client}>
      <WorkspaceContainer>
        <OrganizationContainer>
          <UsersContainer>
            <ProjectsContainer>
              <TierSelectionContainer>
                <SubNavigationContainer>
                  <CreateWorkspace />
                  <Outlet />
                </SubNavigationContainer>
              </TierSelectionContainer>
            </ProjectsContainer>
          </UsersContainer>
        </OrganizationContainer>
      </WorkspaceContainer>
    </ApolloProvider>
  )
}

function App() {
  const { isReady, isAppSignedIn } = usePlatform()
  const { accessToken } = useZippieIdContext()

  const [isAppReady, setIsAppReady] = useState(false)

  useEffect(() => {
    if (!isReady) {
      return
    }
    if (!isAppSignedIn) {
      return setIsAppReady(true)
    }

    if (accessToken) {
      return setIsAppReady(true)
    }
  }, [isReady, isAppSignedIn, accessToken])

  const loggedInRouter = useMemo(
    () =>
      createBrowserRouter(
        createRoutesFromElements(
          <>
            <Route path="/transfer_ownership/:transferWorkspaceId" element={<TransferOwnershipInviteView />} />

            <Route path="*" element={<RootLayout />} errorElement={<RootBoundary />}>
              <Route path="admin" element={<Layout />}>
                <Route path={routes.airImpact} element={<SuperAdministrators />} />
                <Route path={routes.createSuperAdminRoute} element={<CreateSuperAdministrator />} />
                <Route path="*" element={<AdminView />} />
              </Route>
              <Route path=":workspaceId/tier-selection" element={<TierSelectionView />} />
              <Route path=":workspaceId" element={<Layout />}>
                <Route
                  path=""
                  element={
                    <PreloadWrapper
                      component={<AllProjectsView />}
                      componentsToPreload={[AccountSettingsView, WorkspaceView, PeopleView]}
                    />
                  }
                />
                <Route path={`${routes.projectRoute}/*`} element={<ProjectView />} />
                <Route
                  path={routes.peopleRoute}
                  element={
                    <PreloadWrapper
                      component={<PeopleView />}
                      componentsToPreload={[AccountSettingsView, WorkspaceView, AllProjectsView, PersonView]}
                    />
                  }
                />
                <Route
                  path={routes.personRoute}
                  element={
                    <PreloadWrapper
                      component={<PersonView />}
                      componentsToPreload={[AccountSettingsView, WorkspaceView, AllProjectsView, PeopleView]}
                    />
                  }
                />
                <Route
                  path={`${routes.workspaceRoute}/*`}
                  element={
                    <PreloadWrapper
                      component={<WorkspaceView />}
                      componentsToPreload={[AccountSettingsView, AllProjectsView, PeopleView]}
                    />
                  }
                />
                <Route
                  path={`${routes.accountSettings}`}
                  element={
                    <PreloadWrapper
                      component={<AccountSettingsView />}
                      componentsToPreload={[WorkspaceView, AllProjectsView, PeopleView]}
                    />
                  }
                />
              </Route>
            </Route>
          </>,
        ),
      ),
    [],
  )

  const nonLoggedInRouter = useMemo(
    () =>
      createBrowserRouter(
        createRoutesFromElements(
          <>
            <Route
              path="/"
              element={<PreloadWrapper component={<WelcomeView />} componentsToPreload={[AllProjectsView]} />}
              errorElement={<RootBoundary />}
            />
            <Route
              path="/invite/:inviteCode"
              element={<PreloadWrapper component={<WelcomeView />} componentsToPreload={[AllProjectsView]} />}
              errorElement={<RootBoundary />}
            />
            <Route
              path="/invite_owner/:inviteCode"
              element={<PreloadWrapper component={<WelcomeView />} componentsToPreload={[AllProjectsView]} />}
              errorElement={<RootBoundary />}
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </>,
        ),
      ),
    [],
  )

  if (!isAppReady) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          height: '100vh',
        }}
      >
        <LoadingProgress />
      </Box>
    )
  }

  return (
    <>
      <SimpleSnackbar />

      {accessToken ? <RouterProvider router={loggedInRouter} /> : <RouterProvider router={nonLoggedInRouter} />}
    </>
  )
}

export const getDidEnv = () => {
  const { origin } = window.location
  if (origin.includes('localhost')) {
    return 'https://did.dev.zippie.com/v2'
  }
  if (origin.includes('dashboard2.dev')) {
    return 'https://did.test.zippie.com/v2'
  }
  if (origin.includes('dev')) {
    return 'https://did.dev.zippie.com/v2'
  }
  if (origin.includes('test')) {
    return 'https://did.dev.zippie.com/v2'
  }
  return 'https://did.zippie.com/v2'
}

function Fallback() {
  const [show, setShow] = useState(false)

  useEffect(() => {
    const timeout = setTimeout(() => setShow(true), 500) // delay in ms
    return () => clearTimeout(timeout)
  }, [])

  return show ? (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        height: '100vh',
      }}
    >
      <LoadingProgress />
    </Box>
  ) : null
}

// eslint-disable-next-line import/no-anonymous-default-export
export default function () {
  const cssLink = `${window.location.origin}/style.css`
  return (
    <SnackBarContainer>
      <Suspense fallback={<Fallback />}>
        <PlatformProvider
          agentUri={getDidEnv()}
          clientId="Airimpact"
          config={{
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            theme: customTheme,
            loadingType: LoadingType.circular,
            cssLink,
          }}
        >
          <StyledEngineProvider injectFirst>
            <TranslationsProvider>
              <ThemeProvider theme={theme}>
                <ZippieIdProvider>
                  <App />
                </ZippieIdProvider>
              </ThemeProvider>
            </TranslationsProvider>
          </StyledEngineProvider>
        </PlatformProvider>
      </Suspense>
    </SnackBarContainer>
  )
}
